import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface DataPoint {
  date: string;
  value: number;
}

interface Props {
  primaryData: DataPoint[];
  primaryMetric: string;
  secondaryData?: DataPoint[];
  secondaryMetric?: string | null;
}

const metricLabels: Record<string, string> = {
  views: "Views",
  likes: "Likes",
  replies: "Replies",
  reposts: "Reposts",
  quotes: "Quotes",
  new_followers: "New Followers",
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatValue(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return n.toString();
}

/** Custom tooltip with clear text on dark bg */
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;

  // Find the original date from the first payload entry
  const originalDate = payload[0]?.payload?._date;

  return (
    <div className="rounded-lg bg-black px-4 py-3 shadow-xl">
      <p className="text-xs font-medium text-neutral-400 mb-2">
        {originalDate ? formatFullDate(originalDate) : label}
      </p>
      {payload.map((entry: any, i: number) => (
        <div key={i} className="flex items-center gap-2 mt-1">
          <div
            className="h-2.5 w-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-neutral-300">
            {entry.name}:
          </span>
          <span className="text-sm font-semibold text-white tabular-nums">
            {Number(entry.value).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DailyChart({
  primaryData,
  primaryMetric,
  secondaryData,
  secondaryMetric,
}: Props) {
  if (primaryData.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-neutral-400">
        No data for this period
      </div>
    );
  }

  const hasDual = secondaryMetric && secondaryData && secondaryData.length > 0;

  // Build a map of secondary data by date for merging
  const secondaryMap = new Map<string, number>();
  if (hasDual) {
    for (const d of secondaryData) {
      secondaryMap.set(d.date, d.value);
    }
  }

  // Merge primary + secondary into chart data
  const chartData = primaryData.map((d) => ({
    name: formatDate(d.date),
    _date: d.date, // preserve for tooltip
    [primaryMetric]: d.value,
    ...(hasDual
      ? { [secondaryMetric!]: secondaryMap.get(d.date) ?? 0 }
      : {}),
  }));

  const primaryLabel = metricLabels[primaryMetric] || primaryMetric;
  const secondaryLabel = hasDual
    ? metricLabels[secondaryMetric!] || secondaryMetric!
    : "";

  // Bar radius for rounded tops
  const barRadius: [number, number, number, number] = [3, 3, 0, 0];

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
          barCategoryGap="20%"
          barGap={2}
        >
          <CartesianGrid
            strokeDasharray="none"
            stroke="#f0f0f0"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatValue}
            width={48}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "#f5f5f5", radius: 4 }}
          />
          {hasDual && (
            <Legend
              verticalAlign="top"
              align="right"
              height={28}
              iconType="square"
              iconSize={10}
              formatter={(value: string) => (
                <span className="text-xs text-neutral-500 ml-1">{value}</span>
              )}
            />
          )}
          <Bar
            dataKey={primaryMetric}
            name={primaryLabel}
            fill="#000000"
            radius={barRadius}
            maxBarSize={hasDual ? 28 : 48}
          />
          {hasDual && (
            <Bar
              dataKey={secondaryMetric!}
              name={secondaryLabel}
              fill="#c4c4c4"
              radius={barRadius}
              maxBarSize={28}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
